import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** When true the option is shown greyed-out and can't be toggled. */
  disabled?: boolean;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  /** When set, the trigger shows this label + a small selected-count badge
   *  instead of listing each selected option as a removable pill. */
  triggerLabel?: string;
  /** When provided, the search box is server-driven: the component stops
   *  filtering `options` locally (the parent is expected to return the matching
   *  results) and calls this with the raw query on every keystroke + on reset.
   *  Use for large datasets that can't be fully preloaded (e.g. all products). */
  onSearchChange?: (query: string) => void;
  /** Shows a "Searching…" state in the list. Only meaningful with onSearchChange. */
  loading?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results',
  className,
  disabled,
  triggerLabel,
  onSearchChange,
  loading,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const serverSearch = !!onSearchChange;

  // Reset the query both locally and (in server mode) upstream so the parent's
  // result set returns to its default page.
  const resetQuery = () => {
    setQuery('');
    if (serverSearch) onSearchChange('');
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        resetQuery();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // In server-search mode the parent already returns the matching results, so
  // we render `options` as-is. Otherwise filter locally over the preloaded set.
  const filtered = useMemo(() => {
    if (serverSearch) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, serverSearch]);

  const selectedOptions = useMemo(() => options.filter((o) => selected.includes(o.value)), [options, selected]);
  // `selected` is the source of truth for counts — in server-search mode the
  // selected items may not be in the current results page, so counting
  // `selectedOptions` would undercount. Counts always use `selected.length`.
  const selectedCount = selected.length;

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) resetQuery();
          setOpen(!open);
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
          'border-input bg-white hover:bg-muted/40',
          'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {triggerLabel !== undefined ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
              {triggerLabel}
              {selectedCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-foreground px-1.5 text-[11px] font-semibold text-background">
                  {selectedCount}
                </span>
              )}
            </span>
          ) : selectedOptions.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            selectedOptions.map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              >
                {o.label}
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o.value);
                  }}
                  className="rounded-full hover:bg-primary/20"
                  aria-label={`Remove ${o.label}`}
                >
                  <X className="h-3 w-3" />
                </span>
              </span>
            ))
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {selectedCount > 0 && (
            <span
              role="button"
              tabIndex={-1}
              onClick={clearAll}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear all"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-border bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (serverSearch) onSearchChange(e.target.value);
              }}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">Searching…</div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">{emptyMessage}</div>
            ) : (
              filtered.map((o) => {
                const isSelected = selected.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    disabled={o.disabled}
                    onClick={() => !o.disabled && toggle(o.value)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                      o.disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-muted',
                      isSelected && !o.disabled && 'bg-primary/5',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-white',
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{o.label}</span>
                  </button>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-xs">
            <span className="text-muted-foreground">{selectedCount} selected</span>
            <div className="flex items-center gap-3">
              {filtered.some((o) => !o.disabled && !selected.includes(o.value)) && (
                <button
                  type="button"
                  // Union with the existing selection so "Select all" adds the
                  // currently-shown results without dropping selections made on
                  // other search pages (server-search mode). Disabled options
                  // (e.g. locations outside the active filter) are skipped.
                  onClick={() =>
                    onChange(
                      Array.from(new Set([...selected, ...filtered.filter((o) => !o.disabled).map((o) => o.value)])),
                    )
                  }
                  className="font-medium text-primary hover:underline"
                >
                  Select all{serverSearch ? ' shown' : ''}
                </button>
              )}
              {selectedCount > 0 && (
                <button type="button" onClick={() => onChange([])} className="font-medium text-primary hover:underline">
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
